import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Phone, TimerReset } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { fetchAdvisorTrials } from '@/data/billing';

const STATE_STYLE: Record<string, string> = {
  expired: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  ending_soon: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
};

export default function TrialWatchCard() {
  const { data } = useQuery({
    queryKey: ['advisor-trials'],
    queryFn: fetchAdvisorTrials,
    retry: false,
  });

  const needAttention = (data ?? []).filter(
    (row) => row.trial_state === 'expired' || row.trial_state === 'ending_soon',
  );

  if (needAttention.length === 0) return null;

  return (
    <Card className="border-amber-500/30" data-testid="trial-watch-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <TimerReset className="h-4 w-4 text-amber-500" />
          Trials to follow up
        </CardTitle>
        <CardDescription>
          Call these clients before their free trial runs out.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {needAttention.map((row) => (
          <div
            key={row.organization_id}
            className="flex flex-wrap items-center gap-3 rounded-md border p-2.5"
            data-testid={`trial-row-${row.organization_id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium truncate">{row.name}</span>
                <Badge variant="outline" className={STATE_STYLE[row.trial_state ?? ''] ?? ''}>
                  {row.trial_state === 'expired'
                    ? 'expired'
                    : `${row.days_left} day${row.days_left === 1 ? '' : 's'} left`}
                </Badge>
              </div>
              {row.contact_name && (
                <p className="text-xs text-muted-foreground truncate">{row.contact_name}</p>
              )}
            </div>
            {row.contact_phone && (
              <a
                href={`tel:${row.contact_phone.replace(/\s+/g, '')}`}
                className="inline-flex items-center gap-1 text-sm underline"
                data-testid={`trial-phone-${row.organization_id}`}
              >
                <Phone className="h-3 w-3" />
                {row.contact_phone}
              </a>
            )}
            {row.advisor_client_id && (
              <Link
                to={`/advisor/clients/${row.advisor_client_id}/overview`}
                className="text-xs underline text-muted-foreground"
              >
                Open client
              </Link>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
